import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { omit } from 'ramda';
import { type ReactElement, useEffect, useState } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { FormProvider, useForm } from 'react-hook-form';

import { CheckboxField } from '@/app/components/client/checkbox-field';
import { StructuredContent } from '@/app/components/client/structured-content';
import { TextField } from '@/app/components/client/text-field';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { useFormRequest } from '@/hooks/use-form-request';
import { pushToDataLayer } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import { getNurturingSchema } from '@/lib/utils/form-validations/forms/nurturing-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';

type FormData = {
  cToken: string;
  newsletter: boolean;
} & EmailInput;

export type FormContent = {
  buttonText: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  legalText: StructuredTextGraphQlResponse;
  newsletterEnabled: boolean;
  newsletterLabel: string;
  successMessage: string;
  salesforceSource: string;
};

type Props = {
  formContent: FormContent;
} & FlexProps;

export const Form = (props: Props): ReactElement => {
  const {
    formContent: {
      buttonText,
      emailLabel,
      emailPlaceholder,
      emailValidationMessages,
      errorMessage,
      legalText,
      newsletterEnabled,
      newsletterLabel,
      successMessage,
      salesforceSource,
    },
    ...flexProps
  } = props;

  const formRequest = useFormRequest();

  const {
    params: { language },
  } = usePageContext();

  const [formFeedback, setFormFeedback] = useState<{
    status: 'error' | 'success';
    message: string;
  } | null>();

  const formSchema = getNurturingSchema({
    emailValidationMessages,
    submitError: errorMessage,
  });

  const formMethods = useForm<FormData>({
    defaultValues: { newsletter: false },
    // Start validating on submit to not interfere with closing the modal
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = formMethods;

  useEffect(() => {
    if (isDirty && formFeedback?.status === 'success') {
      setFormFeedback(null);
    }
  }, [formFeedback?.status, isDirty]);

  const setCaptchaToken = (token: string) => setValue('cToken', token);
  const cToken = watch('cToken');

  const onSubmit = async (formData: FormData): Promise<void> => {
    setFormFeedback(null);
    const { email, newsletter } = formData;
    try {
      await formRequest({
        data: {
          ...omit(['newsletter'], formData),
          emailOptIn: newsletterEnabled ? newsletter : true,
          language,
          source: salesforceSource,
        },
        formType: 'nurturing',
      });
      pushToDataLayer({ email, event: 'popup_form_lead' });
      setFormFeedback({ status: 'success', message: successMessage });
      reset();
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Flex
        as="form"
        direction="column"
        gap={2}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        {...flexProps}
      >
        <TextField
          error={errors.email?.message}
          isRequired
          label={emailLabel}
          name="email"
          placeholder={emailPlaceholder}
          type="email"
        />

        {!cToken && (
          <TurnstileFormToken
            includeMargin={false}
            language={language}
            onVerify={setCaptchaToken}
          />
        )}

        {newsletterEnabled && (
          <CheckboxField
            error={errors.newsletter?.message}
            isRequired={false}
            label={newsletterLabel}
            matchCaptcha
            name="newsletter"
          />
        )}

        <Button
          alignSelf="center"
          isLoading={isSubmitting}
          type="submit"
          width={['100%', null, '328px']}
        >
          {buttonText}
        </Button>

        <StructuredContent
          color="text.secondary"
          data={legalText}
          textAlign="center"
          textStyle="m"
        />

        {formFeedback?.status && <FormFeedback {...formFeedback} />}
      </Flex>
    </FormProvider>
  );
};
